import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _typeof from '@babel/runtime/helpers/typeof';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _PropTypes from 'prop-types';
import * as React from 'react';
import { useId } from 'react-id-generator';
import styled from 'styled-components';
import { KEYS } from '../../constants/keys.js';
import { getAdornment } from '../../utils/adornment.js';
import { bindRefs } from '../../utils/react.js';
import { noop } from '../../utils/util.js';
import { Checkbox } from '../Checkbox/Checkbox.js';
import { List } from '../List/List.js';
import { ListItem } from '../ListItem/ListItem.js';
import { Radio } from '../Radio/Radio.js';
import { jsx, jsxs } from 'react/jsx-runtime';

var _excluded = ["enableSelectAll", "filterOptions", "id", "multi", "onSelectedChange", "optionHeight", "options", "renderOption", "selectedOptions", "scrollRef", "startAdornment"];
var SELECT_ALL = "__SELECT_ALL__";
var StyledAdornmentContainer = styled.span.withConfig({
  displayName: "Listbox__StyledAdornmentContainer",
  componentId: "sc-lzxtms-0"
})({
  display: "flex",
  marginInlineEnd: 10
});

var toggleArrayValue = function toggleArrayValue(arrayList, arrayValue) {
  return arrayList.includes(arrayValue) ? arrayList.filter(function (el) {
    return el !== arrayValue;
  }) : [].concat(_toConsumableArray(arrayList), [arrayValue]);
};

var Listbox = /*#__PURE__*/React.forwardRef(function Component(props, ref) {
  var enableSelectAll = props.enableSelectAll,
      _props$filterOptions = props.filterOptions,
      filterOptions = _props$filterOptions === void 0 ? function () {
    return true;
  } : _props$filterOptions,
      id = props.id,
      multi = props.multi,
      onSelectedChange = props.onSelectedChange,
      optionHeight = props.optionHeight,
      _props$options = props.options,
      optionsProp = _props$options === void 0 ? [] : _props$options,
      renderOption = props.renderOption,
      _props$selectedOption = props.selectedOptions,
      selectedOptions = _props$selectedOption === void 0 ? [] : _props$selectedOption,
      scrollRefProp = props.scrollRef,
      startAdornment = props.startAdornment,
      rest = _objectWithoutProperties(props, _excluded);

  var canSelectAll = multi && enableSelectAll;
  var selectAllOption = {
    value: SELECT_ALL
  };
  var options = canSelectAll ? [selectAllOption].concat(_toConsumableArray(optionsProp)) : optionsProp;
  var filteredOptions = options.filter(function (option, i) {
    return canSelectAll && i === 0 || filterOptions(option);
  });
  var filteredOptionsLength = filteredOptions.length;
  var isFiltered = filteredOptionsLength !== options.length;

  var _useId = useId(1, "listbox-"),
      _useId2 = _slicedToArray(_useId, 1),
      defaultId = _useId2[0];

  var listboxId = id || defaultId;
  var optionProps = React.useMemo(function () {
    return Array.from({
      length: filteredOptionsLength
    }, function (v, i) {
      return {
        id: "".concat(listboxId, "-option-").concat(i),
        ref: /*#__PURE__*/React.createRef()
      };
    });
  }, [filteredOptionsLength, listboxId]);
  var listRef = React.useRef();
  var scrollRef = scrollRefProp || listRef;

  var scrollToIndex = function scrollToIndex(nextIndex) {
    var listElem = scrollRef.current;
    var elem = optionProps[nextIndex].ref.current;
    var elemTop = elem.offsetTop - listElem.offsetTop;
    var elemBottom = elemTop + elem.offsetHeight;
    var shouldScrollDown = elemBottom >= listElem.offsetHeight + listElem.scrollTop;
    var shouldScrollUp = elemTop <= listElem.scrollTop;
    elem.focus();

    if (nextIndex === 0 && shouldScrollUp) {
      listElem.scrollTop = 0;
      return;
    }

    if (shouldScrollDown) {
      listElem.scrollTop = elem.offsetTop - listElem.offsetHeight + elem.offsetHeight;
    }

    if (shouldScrollUp) {
      listElem.scrollTop = elemTop;
    }
  };

  var handleSelect = function handleSelect(index) {
    var option = filteredOptions[index];
    if (!onSelectedChange || option.disabled) return;
    var nextSelectedOptions;

    if (option.value === SELECT_ALL) {
      nextSelectedOptions = optionsProp.length === selectedOptions.length ? [] : optionsProp;
    } else {
      nextSelectedOptions = multi ? toggleArrayValue(selectedOptions, option) : [option];
    }

    onSelectedChange(nextSelectedOptions);
  };

  var handleListItemKeyDown = function handleListItemKeyDown(e, index) {
    switch (e.key) {
      case KEYS.ARROW_UP:
        {
          scrollToIndex(Math.max(index - 1, 0));
          e.preventDefault();
          break;
        }

      case KEYS.ARROW_DOWN:
        {
          scrollToIndex(Math.min(index + 1, filteredOptions.length - 1));
          e.preventDefault();
          break;
        }

      case KEYS.ENTER:
        {
          if (!multi) handleSelect(index);
          break;
        }

      case KEYS.SPACE:
        {
          if (multi) handleSelect(index);
          e.preventDefault();
          break;
        }
    }
  };

  return /*#__PURE__*/jsx(List, _objectSpread(_objectSpread({}, rest), {}, {
    "aria-multiselectable": multi ? true : void 0,
    id: listboxId,
    onFocus: function onFocus(e) {
      if (e.target === listRef.current) optionProps[0].ref.current.focus();
      if (props.onFocus) props.onFocus(e);
    },
    ref: bindRefs(ref, listRef),
    role: "listbox",
    tabIndex: -1,
    children: filteredOptions.map(function (option, index) {
      var isSelectAll = canSelectAll && index === 0;
      var sharedListItemProps = {
        id: optionProps[index].id,
        key: optionProps[index].id,
        onClick: function onClick() {
          return handleSelect(index);
        },
        onKeyDown: function onKeyDown(e) {
          return handleListItemKeyDown(e, index);
        },
        ref: optionProps[index].ref,
        role: "option",
        tabIndex: -1
      };
      var sharedCheckboxProps = {
        presentational: true,
        readOnly: true,
        role: "none"
      };
      var hasIcon = _typeof(startAdornment) === "object";

      if (isSelectAll) {
        var ariaSelected = selectedOptions.length === optionsProp.length;
        var indeterminate = selectedOptions.length > 0 && !ariaSelected;
        return !isFiltered ? /*#__PURE__*/jsxs(ListItem, _objectSpread(_objectSpread({
          variant: optionHeight,
          "aria-live": "polite",
          "aria-selected": ariaSelected,
          border: true
        }, sharedListItemProps), {}, {
          children: [/*#__PURE__*/jsx(StyledAdornmentContainer, {
            children: /*#__PURE__*/jsx(Checkbox, _objectSpread({
              checked: ariaSelected,
              indeterminate: indeterminate
            }, sharedCheckboxProps))
          }), hasIcon && /*#__PURE__*/jsx(StyledAdornmentContainer, {
            children: getAdornment(startAdornment, {
              iconSize: 16
            })
          }), ariaSelected ? "Deselect all" : "Select all"]
        })) : null;
      } else {
        var _ariaSelected = selectedOptions.includes(option);

        return /*#__PURE__*/jsxs(ListItem, _objectSpread(_objectSpread(_objectSpread({
          variant: optionHeight
        }, option), sharedListItemProps), {}, {
          "aria-selected": _ariaSelected,
          children: [function () {
            if (multi) {
              return /*#__PURE__*/jsx(StyledAdornmentContainer, {
                children: /*#__PURE__*/jsx(Checkbox, _objectSpread({
                  checked: _ariaSelected
                }, sharedCheckboxProps))
              });
            } else if (startAdornment === "radio") {
              return /*#__PURE__*/jsx(StyledAdornmentContainer, {
                children: /*#__PURE__*/jsx(Radio, {
                  checked: _ariaSelected,
                  onChange: noop
                })
              });
            }
          }(), hasIcon && /*#__PURE__*/jsx(StyledAdornmentContainer, {
            children: getAdornment(startAdornment, {
              iconSize: 16
            })
          }), renderOption(option)]
        }));
      }
    })
  }));
});
Listbox.propTypes = {
  "enableSelectAll": _PropTypes.bool,
  "filterOptions": _PropTypes.func,
  "id": _PropTypes.string,
  "multi": _PropTypes.bool,
  "onFocus": _PropTypes.func,
  "onSelectedChange": _PropTypes.func,
  "optionHeight": _PropTypes.oneOf(["short", "tall", "variable"]),
  "options": _PropTypes.arrayOf(_PropTypes.object),
  "renderOption": _PropTypes.func.isRequired,
  "scrollRef": _PropTypes.shape({
    current: _PropTypes.object
  }),
  "selectedOptions": _PropTypes.arrayOf(_PropTypes.object),
  "startAdornment": function checkAdornment(props, propName, componentName) {
    var isReactNodeType = function isReactNodeType(el) {
      var REACT_ELEMENT_TYPE = typeof Symbol === 'function' && Symbol.for ? Symbol.for('react.element') : 0xeac7;
      var isValidElement = _typeof(el) === 'object' && el !== null && el['\$\$typeof'] === REACT_ELEMENT_TYPE;
      return el === undefined || el === null || typeof el === 'boolean' || typeof el === 'string' || isValidElement;
    };

    var adornment = (propName === 'startAdornment' || propName === 'endAdornment') && props[propName];

    if (!isReactNodeType(adornment)) {
      var name = adornment.displayName || adornment.name || 'Component';

      if (!(name.indexOf('Icon') === 0)) {
        return new Error("Invalid prop \"".concat(propName, "\". Invalid React component with name \"").concat(name, "\" was supplied to \"").concat(componentName, "\". Only Icon components are allowed.\n"));
      }
    }
  }
};
Listbox.displayName = "Listbox";

export { Listbox };
